import Combobox from '@/components/custom/Combobox';
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useSurveyStore } from "@/lib/zustand/useStore";
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from "react-hook-form";

import { BorderBox } from '@/components/atoms/BorderBox';
import { DynamicHeightScrollArea } from '@/components/atoms/DynamicHeightScrollArea';
import { ParameterSelect } from '@/components/atoms/ParameterSelect';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateCrmConfig } from '@/features/crm_config/hooks/useMutation/useUpdateCrmConfig';
import { useUpdateSurvey } from '@/features/survey/hooks/useMutation/useUpdateSurvey';
import { matchConfigurationDataGrouped } from '@/lib/utils/crm/matchConfigurationData';
import { Loader } from 'lucide-react';
import { Link, useParams } from "react-router-dom";
import { editSurveySchema, EditSurveySchemaType } from '.';
import { useCrmConfigsByCrmProjectId } from '@/features/crm_config/hooks/useQuery/useCrmConfigsByCrmProjectId';
import { useGetSurveyById } from '@/features/survey/hooks/useQuery/useGetSurveyById';

export function EditSurvey() {
    const { orgId } = useParams();
    const { survey } = useSurveyStore();

    const { data: surveyData, isPending: isPendingGetSurveyData } = useGetSurveyById(survey?.id);
    const { data: crmConfigs } = useCrmConfigsByCrmProjectId(surveyData?.project?.crm_project_id || undefined)

    const fullConfig = useMemo(() => {
        if (!surveyData || !surveyData.crm_config || !surveyData.crm) return undefined;
        return matchConfigurationDataGrouped(surveyData.crm, surveyData.crm_config.crm_project, surveyData.crm_config)
    }, [surveyData])
    const [parameters, setParameters] = useState(fullConfig?.withDefaultConfiguration?.parameter?.survey || [])
    useEffect(() => {
        if (fullConfig) {
            setParameters(fullConfig.withDefaultConfiguration?.parameter?.survey || []);
        }
    }, [fullConfig]);

    const form = useForm<EditSurveySchemaType>({ resolver: zodResolver(editSurveySchema) });

    const resetSettingsToDefault = useCallback(() => {
        form.reset({
            name: survey?.name || '',
            description: survey?.description || '',
            crm_config_id: survey?.crm_config_id || null,

            selected_configuration: {
                parameter: parameters?.map((parameter) => ({
                    id: parameter.id,
                    value: parameter.value || ''
                })) || [],
            },
        })
    }, [form, survey?.name, survey?.description, survey?.crm_config_id, parameters])
    useEffect(() => {
        resetSettingsToDefault();
    }, [fullConfig, resetSettingsToDefault]);

    const { mutateAsync: updateSurvey, isPending: isPendingUpdateSurvey } = useUpdateSurvey();
    const { mutateAsync: updateCrmConfig, isPending: isPendingUpdateCrmSurvey } = useUpdateCrmConfig();

    const onSubmit = async (data: EditSurveySchemaType) => {
        if (!survey) {
            throw new Error("Survey not found");
        }

        // TODO: Use different notification/toast library. Stacking of messages is needed.
        await updateSurvey({
            data: {
                id: survey.id,
                name: data.name,
                description: data.description,
                crm_config_id: data.crm_config_id || null,
            }
        })
            .then(() => {
                SuccessPopup("Survey updated successfully");
            })
            .catch(() => {
                // TODO: Error message based on the error response
                ErrorPopup("Something went wrong while updating the survey.");
            })

        if (data.crm_config_id) {
            await updateCrmConfig({
                id: data.crm_config_id || undefined,
                selected_configuration: data.selected_configuration
            })
                .then(() => {
                    SuccessPopup("Survey's crm updated successfully");
                })
                .catch(() => {
                    // TODO: Error message based on the error response
                    ErrorPopup("Something went wrong while updating the survey's crm.");
                })
        }
    };
    return (
        <>
            {(isPendingUpdateSurvey || isPendingUpdateCrmSurvey) && (<LoaderWithBackdrop />)}
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex items-stretch flex-1 gap-4">
                    <div className="flex flex-col flex-1">
                        <div className="flex flex-col flex-1 gap-2">
                            <p className="mb-2 text-xs">Basic configuration</p>
                            <div className="flex flex-col gap-2 px-2">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <Label className="mb-2">Survey Name</Label>
                                            <FormControl>
                                                <Input
                                                    placeholder="Enter survey name"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="description"
                                    render={({ field }) => (
                                        <FormItem>
                                            <Label className="mb-2">Project description</Label>
                                            <FormControl>
                                                <Textarea
                                                    placeholder="Survey description"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <Separator orientation="vertical" />

                    <div className="flex flex-col flex-1">
                        {(() => {
                            if (isPendingGetSurveyData) return (
                                <div className="grid h-full place-items-center">
                                    <Loader className="animate-spin" />
                                </div>
                            )
                            if (!crmConfigs?.length) return (
                                <div className="grid h-full place-items-center">
                                    <div className="flex flex-col items-center">
                                        <p className="text-sm text-muted-foreground">No CRM configurations found for this survey.</p>
                                        <p className="text-sm text-muted-foreground">
                                            Go to the
                                            <Button
                                                variant="link"
                                                className='px-1'
                                                asChild
                                            >
                                                <Link to={`/organization/${orgId}/project/${survey?.project_id}/manage-project/edit`}>project settings</Link>
                                            </Button>
                                            and select CRM project to configure the survey.
                                        </p>

                                    </div>
                                </div>
                            )

                            return (
                                <div className="flex flex-col flex-1 gap-2">
                                    <DynamicHeightScrollArea
                                        offset={80}
                                    >
                                        <p className="mb-2 text-xs">CRM configuration</p>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex flex-col gap-2 px-2 pt-1">
                                                <div className="m-0 mb-3">
                                                    {(crmConfigs) && (
                                                        <>
                                                            <FormField
                                                                control={form.control}
                                                                name="crm_config_id"
                                                                render={({ field }) => (
                                                                    <FormItem>
                                                                        <Label className="mb-2">CRM Config</Label>
                                                                        <FormControl>
                                                                            <div className="">
                                                                                <Combobox
                                                                                    items={[
                                                                                        { label: "Clear selection", value: "" },
                                                                                        ...(
                                                                                            crmConfigs?.map((config, i) => ({ label: `${i + 1}. ${config.name}`, value: `${config.id}` }))
                                                                                            ?? []
                                                                                        )
                                                                                    ]}
                                                                                    setSelectedValue={(v) => field.onChange(Number(v))}
                                                                                    selectedValue={String(field.value) || null}
                                                                                    triggerLabel="Select CRM Project"
                                                                                />
                                                                                <div className="flex justify-end">
                                                                                    <Button
                                                                                        variant="link"
                                                                                        className="p-0 text-xs"
                                                                                        asChild
                                                                                    >
                                                                                        <Link
                                                                                            to={`/organization/${orgId}/manage-organization?currentTab=crm_config&crmId=${survey?.project?.crm_id}&crmProjectId=${survey?.project?.crm_project_id}&crmConfigId=${survey?.crm_config_id}`}
                                                                                        >
                                                                                            Go to CRM Config
                                                                                        </Link>
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        </FormControl>
                                                                        <FormMessage />
                                                                    </FormItem>
                                                                )}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {parameters?.length !== 0 && (
                                                <div className="flex justify-between pe-4">
                                                    <div className="flex items-center">
                                                        <p className="mb-2 text-xs">Parameters</p>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex flex-col gap-2 px-2">
                                                <div className="flex flex-col gap-2">
                                                    <div className="">
                                                        {parameters?.map((parameter, i) => (
                                                            <div className=""
                                                                key={parameter.id}
                                                            >
                                                                <FormField
                                                                    control={form.control}
                                                                    name={`selected_configuration.parameter.${i}.value`}
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <Label className="mb-1">{parameter.name}</Label>
                                                                            <FormControl>
                                                                                <ParameterSelect
                                                                                    parameterData={parameter}
                                                                                    value={field.value}
                                                                                    onChange={field.onChange}
                                                                                    onCreate={(newValue) => {
                                                                                        setParameters((prev) => {
                                                                                            const newParameter = {
                                                                                                ...parameter,
                                                                                                options: [
                                                                                                    ...(parameter.options || []),
                                                                                                    { key: newValue, value: newValue }
                                                                                                ],
                                                                                            };
                                                                                            return [...prev, newParameter];
                                                                                        })
                                                                                    }}
                                                                                />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {(survey?.crm_id && survey?.crm_config_id) && (
                                                    <BorderBox variant='info' size='sm'>
                                                        <p className="text-xs">
                                                            <span>If you would like to add additional parameters to this configuration, </span>
                                                            <Link to={`/organization/${orgId}/manage-organization?crmId=${survey.project?.crm_id}&secondaryTab=parameters&parametersTab=survey`}>
                                                                <span className="font-bold hover:underline">click here.</span>
                                                            </Link>
                                                        </p>
                                                    </BorderBox>
                                                )}
                                            </div>
                                        </div>
                                    </DynamicHeightScrollArea>

                                </div>
                            )
                        })()}
                        <div className="flex justify-end gap-2">
                            <Button size="sm" variant="secondary" type="button" onClick={resetSettingsToDefault}>Reset</Button>
                            <Button size="sm" type="submit">Submit</Button>
                        </div>
                    </div>
                </form>
            </Form>
        </>
    )
}
